// engine-exception: end-of-day reconciliation folds two sources (capture sessions and time entries) into one timeline with per-gap log actions; it is a workflow over an aggregate read, not a resource list
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAction } from '../../engine/api/hooks';
import { Badge, Chip, EmptyState, ErrorBanner, PageHeader, Skeleton } from '../../engine/design/primitives/Misc';
import { Button } from '../../engine/design/primitives/Button';
import { Field, Input } from '../../engine/design/primitives/Input';
import { Icon } from '../../engine/design/icons';
import { formatDateTime, formatMinutes } from '../../lib/format';
import { formatSeconds, useDay } from './lib/timeCapture';

// "My day" (docs/servicedesk-v2-proposal.md P6): the technician's captured time beside the time
// they actually logged, so closing the day is filling the gaps rather than reconstructing it from
// memory. Each gap logs with one click at the minutes the tenant's rounding policy suggests.

const today = () => new Date().toISOString().slice(0, 10);

export function MyDayScreen() {
  const navigate = useNavigate();
  const [date, setDate] = useState(today);
  const day = useDay(date);
  const log = useAction<{ minutes: number }>('time-captures', 'log', { toast: { success: (r) => `Logged ${(r as { minutes: number }).minutes} minutes` } });
  const discard = useAction('time-captures', 'discard', { toast: { success: 'Discarded' } });

  if (day.error) return <ErrorBanner error={day.error} />;
  const d = day.data;

  return (
    <div className="hf-page">
      <PageHeader
        title="My day"
        subtitle="What you captured, what you logged, and what is still missing."
        actions={
          <Field label="Date" id="sd-day-date">
            <Input id="sd-day-date" type="date" value={date} size="sm" onChange={(e) => setDate(e.target.value || today())} />
          </Field>
        }
      >
        {d && (
          <div className="hf-chiprow" aria-label="Day totals">
            <Chip icon="check">Logged {formatMinutes(d.logged_minutes)}</Chip>
            <Chip icon="banknote">Billable {formatMinutes(d.billable_minutes)}</Chip>
            <Chip icon="timer">Captured {formatMinutes(d.captured_minutes)}</Chip>
            {d.unlogged_minutes > 0 ? (
              <Badge tone="warn">{formatMinutes(d.unlogged_minutes)} not logged</Badge>
            ) : (
              <Badge tone="pos" dot={false}>Nothing outstanding</Badge>
            )}
          </div>
        )}
      </PageHeader>

      {day.isLoading || !d ? (
        <Skeleton lines={8} />
      ) : (
        <div className="hf-stack" style={{ gap: 'var(--s-4)' }}>
          <section className="hf-stack" style={{ gap: 'var(--s-2)' }} aria-label="Unlogged time">
            <h2 className="hf-section-title">Gaps</h2>
            {d.gaps.length === 0 ? (
              <EmptyState title="Every captured minute is logged" icon="check" />
            ) : (
              <ul className="hf-stack" style={{ gap: 'var(--s-2)', listStyle: 'none', padding: 0, margin: 0 }}>
                {d.gaps.map((g) => (
                  <li key={g.session_id} className="hf-card pad hf-between">
                    <div className="hf-stack" style={{ gap: 2 }}>
                      <span className="hf-row" style={{ gap: 'var(--s-1)' }}>
                        {g.ticket_ref ? (
                          <Button variant="link" size="sm" onClick={() => g.ticket_id && navigate(`/tickets/${g.ticket_id}`)}>{g.ticket_ref}</Button>
                        ) : (
                          <span className="hf-muted">No ticket</span>
                        )}
                        {g.running && <Badge tone="accent">Running</Badge>}
                      </span>
                      <span style={{ fontSize: 'var(--t-12)' }}>{g.ticket_summary ?? '—'}</span>
                      <span className="hf-muted hf-num" style={{ fontSize: 'var(--t-11)' }}>
                        <Icon name="timer" size={11} /> {formatSeconds(g.captured_seconds)} captured from {formatDateTime(g.started_at)}
                      </span>
                    </div>
                    <div className="hf-row">
                      <Badge tone="muted" dot={false}>logs as {formatMinutes(g.suggested_minutes)}</Badge>
                      <Button size="sm" variant="primary" icon="check" busy={log.isPending}
                        onClick={() => log.mutate({ id: g.session_id, body: {} }, { onSuccess: () => void day.refetch() })}>
                        Log
                      </Button>
                      <Button size="sm" variant="ghost" icon="trash"
                        onClick={() => discard.mutate({ id: g.session_id, body: { reason: 'Not billable work' } }, { onSuccess: () => void day.refetch() })}>
                        Discard
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="hf-stack" style={{ gap: 'var(--s-2)' }} aria-label="Logged time">
            <h2 className="hf-section-title">Logged</h2>
            {d.entries.length === 0 ? (
              <EmptyState title="Nothing logged yet today" icon="timer" />
            ) : (
              <ul className="hf-stack" style={{ gap: 'var(--s-1)', listStyle: 'none', padding: 0, margin: 0 }}>
                {d.entries.map((e) => (
                  <li key={e.id} className="hf-row" style={{ gap: 'var(--s-2)' }}>
                    <span className="hf-num" style={{ minWidth: 64 }}>{formatMinutes(e.minutes)}</span>
                    {e.ticket_ref ? (
                      <Button variant="link" size="sm" onClick={() => e.ticket_id && navigate(`/tickets/${e.ticket_id}`)}>{e.ticket_ref}</Button>
                    ) : (
                      <span className="hf-muted">No ticket</span>
                    )}
                    <Badge tone={e.billable ? 'pos' : 'muted'} dot={false}>{e.billable ? e.charge_type : 'non-billable'}</Badge>
                    <span className="hf-muted" style={{ fontSize: 'var(--t-12)' }}>{e.note ?? ''}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
